import React, { useEffect, useState } from "react";
import { Eye, Moon, Sun, Sparkles, Leaf } from "lucide-react";

// ── Theme cycling ────────────────────────────────────────────────────────────
const THEMES = ["light", "dark", "contrast", "purple", "green"];

function getInitialTheme() {
  if (typeof document === "undefined") return "light";
  const saved = localStorage.getItem("decisionflow-theme");
  if (THEMES.includes(saved)) return saved;
  const current = document.documentElement.getAttribute("data-theme");
  if (THEMES.includes(current)) return current;
  return "light";
}

// ── Font-size cycling ────────────────────────────────────────────────────────
const FONT_SIZES = ["normal", "large", "xl"];

// Visual A sizes rendered at their actual resulting px so the control
// is self-documenting — same pattern as IBM's accessibility widget.
const FONT_A_SIZES = {
  normal: "14px",
  large:  "16px",
  xl:     "18px",
};

function getInitialFontSize() {
  if (typeof document === "undefined") return "normal";
  const current = document.documentElement.getAttribute("data-font-size");
  if (FONT_SIZES.includes(current)) return current;
  const saved = localStorage.getItem("decisionflow-font-size");
  if (FONT_SIZES.includes(saved)) return saved;
  return "normal";
}

/**
 * ThemeButton — C0 update
 *
 * Two self-contained controls side by side:
 *   1. Theme cycle button  — Sun / Moon / Eye icon, cycles light → dark → contrast
 *   2. Font size control   — three "A" characters at their actual rendered sizes,
 *                            active size highlighted with primary token
 *
 * Both write to localStorage and set attributes on <html>.
 * No parent props required.
 *
 * M040 breaking-change note (unchanged):
 *   Parent components that previously passed dark/setDark must remove those
 *   props and rely on the data-theme attribute + localStorage.
 */
export default function ThemeButton({ className = "" }) {
  const [theme, setTheme]       = useState(getInitialTheme);
  const [fontSize, setFontSize] = useState(getInitialFontSize);

  // Sync theme → DOM + localStorage
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("decisionflow-theme", theme);
  }, [theme]);

  // Sync font-size → DOM + localStorage
  useEffect(() => {
    document.documentElement.setAttribute("data-font-size", fontSize);
    localStorage.setItem("decisionflow-font-size", fontSize);
  }, [fontSize]);

  const cycleTheme = () => {
    const index = THEMES.indexOf(theme);
    setTheme(THEMES[(index + 1) % THEMES.length]);
  };

  const cycleFontSize = (size) => setFontSize(size);

  const ThemeIcon = theme === "light" ? Sun : theme === "dark" ? Moon : theme === "purple" ? Sparkles : theme === "green" ? Leaf : Eye;
  const nextTheme = THEMES[(THEMES.indexOf(theme) + 1) % THEMES.length];

  const btnBase =
    "inline-flex items-center justify-center rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition-colors";

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>

      {/* ── Theme cycle button ─────────────────────────────────────────── */}
      <button
        type="button"
        onClick={cycleTheme}
        className={`${btnBase} p-2`}
        aria-label={`Current theme: ${theme}. Switch to ${nextTheme} theme.`}
        title={`Theme: ${theme}. Click for ${nextTheme}.`}
      >
        <ThemeIcon size={16} aria-hidden="true" />
      </button>

      {/* ── Font size control — three A's at actual sizes ──────────────── */}
      <div
        role="group"
        aria-label="Font size"
        className={`${btnBase} gap-0 overflow-hidden p-0`}
        style={{ gap: 0 }}
      >
        {FONT_SIZES.map((size, i) => {
          const isActive = fontSize === size;
          return (
            <button
              key={size}
              type="button"
              onClick={() => cycleFontSize(size)}
              aria-pressed={isActive}
              aria-label={`Font size ${size}`}
              title={`Font size: ${size}`}
              className={[
                "flex items-center justify-center px-2 py-1 transition-colors font-semibold leading-none select-none",
                // Divider between buttons
                i > 0 ? "border-l border-[rgb(var(--color-border))]" : "",
                // Active state uses primary token; inactive uses muted text
                isActive
                  ? "text-[rgb(var(--color-primary))] bg-[rgb(var(--color-surface-muted))]"
                  : "text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]",
              ].join(" ")}
              style={{ fontSize: FONT_A_SIZES[size], lineHeight: 1 }}
            >
              A
            </button>
          );
        })}
      </div>

    </div>
  );
}
